import { AuthDriver } from '@/components/AuthDriver';
import { pushRoute } from '@/lib/breadcrumbs';
import { ThemeProvider } from '@/lib/theme/provider';
import { routes } from '@/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

export function App() {
  const client = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            retry: (failureCount, error) => {
              const maybeStatus = (error as { status?: number } | null)?.status;
              if (maybeStatus === 401 || maybeStatus === 403) return false;
              return failureCount < 2;
            },
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );

  // Construct the router inside the component so each App instance gets a
  // fresh one. Tests can render a sibling component that swaps in a
  // `createMemoryRouter` against the same `routes` export. `<Toaster/>`
  // lives inside `AppShell` (mounted at the layout root) per G2-T7.
  const router = useMemo(() => createBrowserRouter(routes), []);

  // Router's subscribe() is cheaper than useLocation() — fire-and-forget for breadcrumbs.
  useEffect(() => {
    pushRoute(router.state.location.pathname);
    return router.subscribe((state) => {
      pushRoute(state.location.pathname);
    });
  }, [router]);

  // `<AuthDriver/>` is the sole subscriber that turns a `/api/auth/me`
  // 401 into a single `signIn()` call. It MUST sit inside
  // `QueryClientProvider` (it consumes `useAuthMe`). The ~20 components
  // that call `usePrincipal` no longer fire `signinRedirect`
  // themselves, so the fan-out bug class is closed.
  return (
    <ThemeProvider>
      <QueryClientProvider client={client}>
        <AuthDriver />
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
